/* ==========================================================================
   ALLO — comportements communs
   - apparition douce au défilement
   - dégradés des carrousels (selon la position de défilement)
   - bandeaux défilants (vitesse constante quelle que soit la longueur)
   - <allo-video-rail> : vidéos UGC auto (muet, boucle), son au toucher, flèches
   - <allo-reviews> : compteur animé + flèches
   - retours visuels « Ajouté », code promo (bouton Appliquer + Entrée),
     barre d'achat produit (taille synchronisée), bouton WhatsApp au-dessus de la barre
   ========================================================================== */
(() => {
  if (window.AlloTheme) return;
  window.AlloTheme = { ready: true };
  window.AlloReady = true;

  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const debounce = (fn, wait = 120) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  /* ------------------------------------------------------------------
     1. Apparition au défilement
     ------------------------------------------------------------------ */
  const AUTO_REVEAL = '.mdp-bs__header, .mdp-bs__card, .mdp-bs__footer-cta, .mdp-cgrid__head, .mdp-cgrid__card';
  const SCROLLERS = '[data-allo-edge], .allo-vr__rail, .allo-rv__track';
  const revealed = new WeakSet();
  const groupMap = new WeakMap();

  const reveal = (el) => el.classList.add('is-in');

  const revealIO = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const group = groupMap.get(entry.target);
          if (group) {
            group.forEach((el, i) => {
              el.style.setProperty('--reveal-delay', `${Math.min(i, 5) * 70}ms`);
              reveal(el);
            });
          } else {
            reveal(entry.target);
          }
          revealIO.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.12 })
    : null;

  const scanReveal = (scope = document) => {
    if (!root.classList.contains('allo-reveal-on')) return;
    scope.querySelectorAll(AUTO_REVEAL).forEach((el) => {
      if (!el.hasAttribute('data-allo-reveal')) el.setAttribute('data-allo-reveal', '');
    });
    scope.querySelectorAll('[data-allo-reveal]:not(.is-in)').forEach((el) => {
      if (revealed.has(el)) return;
      revealed.add(el);
      if (!revealIO || reduceMotion.matches) return reveal(el);

      const scroller = el.parentElement && el.parentElement.closest(SCROLLERS);
      if (scroller) {
        const list = groupMap.get(scroller) || [];
        if (!list.length) revealIO.observe(scroller);
        list.push(el);
        groupMap.set(scroller, list);
        return;
      }
      const siblings = el.parentElement ? Array.from(el.parentElement.children).filter((c) => c.hasAttribute('data-allo-reveal')) : [el];
      const index = Math.max(0, siblings.indexOf(el));
      el.style.setProperty('--reveal-delay', `${Math.min(index % 8, 6) * 70}ms`);
      revealIO.observe(el);
    });
  };

  /* ------------------------------------------------------------------
     2. Dégradés sur les côtés des carrousels
     ------------------------------------------------------------------ */
  const EDGE_SELECTORS = '[data-allo-edge], .product-list--carousel, .mdp-bs--slider-m .mdp-bs__grid, scroll-carousel';
  const edgeInit = new WeakSet();
  let fadeSize = null;
  const getFade = () => {
    if (fadeSize === null) fadeSize = parseFloat(getComputedStyle(root).getPropertyValue('--allo-edge-fade')) || 48;
    return fadeSize;
  };
  const updateEdge = (el) => {
    const max = el.scrollWidth - el.clientWidth;
    if (max <= 2) {
      el.style.setProperty('--fade-l', '0px');
      el.style.setProperty('--fade-r', '0px');
      return;
    }
    const f = Math.min(getFade(), el.clientWidth / 5);
    const x = Math.abs(el.scrollLeft);
    el.style.setProperty('--fade-l', `${Math.min(f, x)}px`);
    el.style.setProperty('--fade-r', `${Math.min(f, Math.max(0, max - x))}px`);
  };
  const edgeRO = 'ResizeObserver' in window ? new ResizeObserver((entries) => entries.forEach((e) => updateEdge(e.target))) : null;
  const scanEdges = (scope = document) => {
    if (!root.classList.contains('allo-edge-on')) return;
    scope.querySelectorAll(EDGE_SELECTORS).forEach((el) => {
      if (edgeInit.has(el)) return updateEdge(el);
      edgeInit.add(el);
      el.classList.add('allo-edge');
      let ticking = false;
      el.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          updateEdge(el);
          ticking = false;
        });
      }, { passive: true });
      edgeRO && edgeRO.observe(el);
      updateEdge(el);
    });
  };

  /* ------------------------------------------------------------------
     3. Bandeaux défilants
     ------------------------------------------------------------------ */
  const marqueeInit = new WeakSet();
  const setupMarquee = (el) => {
    const halves = el.querySelectorAll('.allo-marquee__half');
    if (halves.length < 2) return;
    const [a, b] = halves;
    const original = Array.from(a.childNodes).map((n) => n.cloneNode(true));
    let guard = 0;
    while (a.scrollWidth > 0 && a.scrollWidth < el.clientWidth * 1.05 && guard < 8) {
      original.forEach((n) => {
        a.appendChild(n.cloneNode(true));
        b.appendChild(n.cloneNode(true));
      });
      guard++;
    }
    const speed = parseFloat(el.dataset.speed) || 40;
    const width = a.scrollWidth;
    if (width) el.style.setProperty('--mq-dur', `${(width / speed).toFixed(2)}s`);
  };
  const scanMarquees = (scope = document) => {
    scope.querySelectorAll('[data-allo-marquee]').forEach((el) => {
      if (marqueeInit.has(el)) return;
      marqueeInit.add(el);
      setupMarquee(el);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => setupMarquee(el));
    });
  };
  window.addEventListener('resize', debounce(() => {
    document.querySelectorAll('[data-allo-marquee]').forEach(setupMarquee);
  }, 250));

  /* ------------------------------------------------------------------
     4. <allo-video-rail>
     ------------------------------------------------------------------ */
  class AlloVideoRail extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('.allo-vr__rail');
      if (!this.rail) return;
      this.items = Array.from(this.rail.querySelectorAll('.allo-vr__item'));
      if (!this.items.length) return;

      const saveData = navigator.connection && navigator.connection.saveData;
      this.autoplay = this.dataset.autoplay === 'true' && !reduceMotion.matches && !saveData;
      this.inView = false;
      this.activeIndex = -1;
      this.ac = new AbortController();
      const opts = { signal: this.ac.signal };

      this.querySelectorAll('.allo-vr__arrow').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.pauseAdvance(6000);
          this.go(parseInt(btn.dataset.dir, 10) || 1);
        }, opts);
      });
      this.rail.addEventListener('scroll', () => this.queueActive(), { passive: true, signal: this.ac.signal });
      this.rail.addEventListener('click', (e) => this.onClick(e), opts);
      this.rail.addEventListener('pointerdown', () => this.pauseAdvance(8000), opts);
      this.addEventListener('mouseenter', () => { this.hovering = true; }, opts);
      this.addEventListener('mouseleave', () => { this.hovering = false; }, opts);
      window.addEventListener('resize', debounce(() => this.updateActive(), 150), opts);
      document.addEventListener('visibilitychange', () => this.sync(), opts);
      this.addEventListener('shopify:block:select', (e) => {
        const item = e.target.closest('.allo-vr__item');
        if (item) this.center(item, true);
      }, opts);

      this.items.forEach((item) => {
        item._visible = false;
        const video = item.querySelector('video');
        const card = item.querySelector('.allo-vid');
        if (!video) return;
        const bar = item.querySelector('.allo-vid__bar span');
        video.muted = true;
        video.addEventListener('timeupdate', () => {
          if (bar && video.duration) bar.style.setProperty('--p', `${(video.currentTime / video.duration) * 100}%`);
        }, opts);
        video.addEventListener('playing', () => card.classList.remove('is-paused'), opts);
        video.addEventListener('pause', () => {
          if (item._visible && this.inView && !document.hidden) card.classList.add('is-paused');
        }, opts);
      });

      if ('IntersectionObserver' in window) {
        this.itemIO = new IntersectionObserver((entries) => {
          entries.forEach((entry) => { entry.target._visible = entry.isIntersecting; });
          this.sync();
        }, { root: this.rail, rootMargin: '0px 15%', threshold: 0.4 });
        this.items.forEach((item) => this.itemIO.observe(item));
        this.sectionIO = new IntersectionObserver(([entry]) => {
          this.inView = entry.isIntersecting;
          this.sync();
        }, { rootMargin: '150px 0px' });
        this.sectionIO.observe(this);
      } else {
        this.items.forEach((item) => { item._visible = true; });
        this.inView = true;
      }

      requestAnimationFrame(() => {
        const n = this.items.length;
        let start = 0;
        if (this.dataset.start === 'middle') start = Math.floor((n - 1) / 2);
        else if (this.dataset.start === 'second') start = Math.min(1, n - 1);
        this.center(this.items[start], false);
        this.updateActive();
        this.sync();
      });

      if (this.dataset.advance === 'true' && !reduceMotion.matches) {
        const delay = (parseFloat(this.dataset.advanceDelay) || 7) * 1000;
        this.timer = setInterval(() => {
          if (!this.inView || this.hovering || document.hidden || Date.now() < (this.holdUntil || 0)) return;
          if (this.items.some((it) => { const v = it.querySelector('video'); return v && !v.muted; })) return;
          const next = this.activeIndex + 1 >= this.items.length ? 0 : this.activeIndex + 1;
          this.center(this.items[next], true);
        }, delay);
      }
    }

    disconnectedCallback() {
      this.ac && this.ac.abort();
      this.itemIO && this.itemIO.disconnect();
      this.sectionIO && this.sectionIO.disconnect();
      clearInterval(this.timer);
      this.items && this.items.forEach((item) => {
        const v = item.querySelector('video');
        v && v.pause();
      });
    }

    pauseAdvance(ms) { this.holdUntil = Date.now() + ms; }

    center(item, smooth) {
      if (!item) return;
      const left = item.offsetLeft - (this.rail.clientWidth - item.offsetWidth) / 2;
      if (smooth && !reduceMotion.matches) this.rail.scrollTo({ left, behavior: 'smooth' });
      else this.rail.scrollLeft = left;
    }

    go(dir) {
      const target = Math.max(0, Math.min(this.items.length - 1, this.activeIndex + dir));
      this.center(this.items[target], true);
    }

    queueActive() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.updateActive();
      });
    }

    updateActive() {
      const mid = this.rail.scrollLeft + this.rail.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      this.items.forEach((item, i) => {
        const d = Math.abs(item.offsetLeft + item.offsetWidth / 2 - mid);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      if (best !== this.activeIndex) {
        this.activeIndex = best;
        this.items.forEach((item, i) => {
          item.classList.toggle('is-active', i === best);
          const v = item.querySelector('video');
          if (i !== best && v && !v.muted) this.setSound(item, false);
        });
      }
      const prev = this.querySelector('.allo-vr__arrow[data-dir="-1"]');
      const next = this.querySelector('.allo-vr__arrow[data-dir="1"]');
      if (prev) prev.disabled = best <= 0;
      if (next) next.disabled = best >= this.items.length - 1;
    }

    load(video) {
      if (video && !video.getAttribute('src') && video.dataset.src) {
        video.src = video.dataset.src;
        video.load();
      }
    }

    play(item) {
      const video = item.querySelector('video');
      if (!video) return;
      this.load(video);
      const p = video.play();
      if (p && p.catch) {
        p.catch(() => item.querySelector('.allo-vid').classList.add('is-paused'));
      }
    }

    sync() {
      this.items.forEach((item) => {
        const video = item.querySelector('video');
        if (!video) return;
        const userUnmuted = !video.muted;
        const shouldPlay = this.inView && item._visible && !document.hidden && (this.autoplay || userUnmuted);
        if (shouldPlay) {
          if (video.paused) this.play(item);
        } else if (!video.paused) {
          video.pause();
        }
        if (!this.autoplay && !userUnmuted) {
          item.querySelector('.allo-vid').classList.toggle('is-paused', video.paused);
        }
      });
    }

    setSound(item, on) {
      this.items.forEach((other) => {
        const v = other.querySelector('video');
        const b = other.querySelector('.allo-vid__sound');
        const isTarget = other === item;
        if (v) v.muted = isTarget ? !on : true;
        if (b) {
          const active = isTarget && on;
          b.setAttribute('aria-pressed', String(active));
          b.setAttribute('aria-label', active ? 'Couper le son' : 'Activer le son');
        }
      });
    }

    onClick(e) {
      if (e.target.closest('.allo-vid__product')) return;
      const item = e.target.closest('.allo-vr__item');
      if (!item) return;
      const video = item.querySelector('video');
      const soundBtn = e.target.closest('.allo-vid__sound');
      const index = this.items.indexOf(item);

      if (!soundBtn && index !== this.activeIndex) {
        this.pauseAdvance(8000);
        this.center(item, true);
        return;
      }
      if (!video) return;
      this.pauseAdvance(15000);
      if (video.paused) {
        this.setSound(item, true);
        this.play(item);
        return;
      }
      this.setSound(item, video.muted);
    }
  }
  if (!customElements.get('allo-video-rail')) customElements.define('allo-video-rail', AlloVideoRail);

  /* ------------------------------------------------------------------
     5. <allo-reviews>
     ------------------------------------------------------------------ */
  const formatNumber = (n) => {
    try { return new Intl.NumberFormat(root.lang || 'fr-FR').format(n); } catch (e) { return String(n); }
  };
  class AlloReviews extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.allo-rv__track');
      this.ac = new AbortController();
      const opts = { signal: this.ac.signal };
      if (this.track) {
        this.querySelectorAll('.allo-rv__arrow').forEach((btn) => {
          btn.addEventListener('click', () => this.scroll(parseInt(btn.dataset.dir, 10) || 1), opts);
        });
        this.track.addEventListener('scroll', () => this.queueArrows(), { passive: true, signal: this.ac.signal });
        window.addEventListener('resize', debounce(() => this.updateArrows(), 150), opts);
        this.addEventListener('shopify:block:select', (e) => {
          const card = e.target.closest('.allo-rv__card');
          if (card) card.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        }, opts);
        requestAnimationFrame(() => this.updateArrows());
      }
      this.setupCounter();
    }

    disconnectedCallback() {
      this.ac && this.ac.abort();
      this.counterIO && this.counterIO.disconnect();
    }

    step() {
      const card = this.track.querySelector('.allo-rv__card');
      if (!card) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const per = Math.max(1, Math.floor((this.track.clientWidth + gap) / (card.offsetWidth + gap)) - 1);
      return per * (card.offsetWidth + gap);
    }

    scroll(dir) {
      this.track.scrollBy({ left: dir * this.step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    queueArrows() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.updateArrows();
      });
    }

    updateArrows() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const prev = this.querySelector('.allo-rv__arrow--prev');
      const next = this.querySelector('.allo-rv__arrow--next');
      if (prev) prev.disabled = this.track.scrollLeft <= 4;
      if (next) next.disabled = this.track.scrollLeft >= max - 4;
    }

    setupCounter() {
      const el = this.querySelector('.allo-rv__count');
      if (!el) return;
      const target = parseInt(el.dataset.count, 10) || 0;
      el.textContent = formatNumber(target);
      if (el.dataset.animate !== 'true' || reduceMotion.matches || !('IntersectionObserver' in window) || target < 2) return;
      el.textContent = formatNumber(0);
      this.counterIO = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        this.counterIO.disconnect();
        const duration = 1600;
        const t0 = performance.now();
        const tick = (now) => {
          const t = Math.min(1, (now - t0) / duration);
          const eased = 1 - Math.pow(1 - t, 3);
          el.textContent = formatNumber(Math.round(target * eased));
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }, { threshold: 0.6 });
      this.counterIO.observe(el);
    }
  }
  if (!customElements.get('allo-reviews')) customElements.define('allo-reviews', AlloReviews);

  /* ------------------------------------------------------------------
     6. Retour visuel « Ajouté » sur les boutons arrondis
     ------------------------------------------------------------------ */
  document.addEventListener('variant:add', (e) => {
    const quickBuy = e.target && e.target.closest && e.target.closest('quick-buy-modal');
    if (quickBuy && typeof quickBuy.hide === 'function') quickBuy.hide();
    const btn = e.target && e.target.querySelector && e.target.querySelector('.allo-qa');
    if (!btn) return;
    btn.classList.add('is-added');
    setTimeout(() => btn.classList.remove('is-added'), 1800);
  });

  /* ------------------------------------------------------------------
     7. Tiroir panier : code promo + état de mise à jour des lignes
     ------------------------------------------------------------------ */
  document.addEventListener('change', (e) => {
    if (e.target.matches && e.target.matches('[data-allo-discount-input]')) e.target.dataset.sent = e.target.value;
  }, true);

  const applyDiscount = (field) => {
    const input = field.querySelector('[data-allo-discount-input]');
    if (!input) return;
    if (!input.value.trim()) return input.focus();
    if (input.dataset.sent === input.value) return;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };
  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('[data-allo-discount-apply]');
    if (btn) applyDiscount(btn.closest('cart-discount-field'));
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' || !e.target.matches || !e.target.matches('[data-allo-discount-input]')) return;
    e.preventDefault();
    applyDiscount(e.target.closest('cart-discount-field'));
  });
  document.addEventListener('line-item:will-change', (e) => {
    if (e.target && e.target.classList) e.target.classList.add('is-updating');
  });

  /* ------------------------------------------------------------------
     8. Barre d'achat produit : choix de taille synchronisé avec la fiche
     ------------------------------------------------------------------ */
  document.addEventListener('change', (e) => {
    const select = e.target.closest && e.target.closest('[data-allo-satc-option]');
    if (!select) return;
    const formId = select.dataset.form;
    const position = select.dataset.alloSatcOption;
    const value = select.value;
    const input = document.querySelector(`input[form="${formId}"][data-option-position="${position}"][value="${value}"]`);
    if (input && !input.disabled) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      const fallback = document.querySelector(`[form="${formId}"][data-option-position="${position}"]:checked`);
      if (fallback) select.value = fallback.value;
    }
  });

  const satc = document.querySelector('product-sticky-add-to-cart.allo-satc');
  if (satc) {
    const updateLift = () => {
      const wa = document.querySelector('.allo-wa');
      let lift = 0;
      if (wa && satc.classList.contains('is-visible')) {
        const inner = satc.querySelector('.allo-satc__inner') || satc;
        const s = inner.getBoundingClientRect();
        const w = wa.getBoundingClientRect();
        const overlap = w.left < s.right + 8 && w.right > s.left - 8;
        if (overlap) lift = Math.round(s.height + 12);
      }
      root.style.setProperty('--allo-satc-lift', `${lift}px`);
    };
    new MutationObserver(updateLift).observe(satc, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('resize', debounce(updateLift, 150));
  }

  /* ------------------------------------------------------------------
     8a. Fenêtre d'achat rapide : correctif du double chargement
     Le thème appelait show() deux fois (clic + attribut "open"), ce qui rechargeait
     le contenu une 2e fois et pouvait remettre la pointure choisie à zéro.
     ------------------------------------------------------------------ */
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('quick-buy-modal').then(() => {
      const QuickBuy = customElements.get('quick-buy-modal');
      if (!QuickBuy || QuickBuy.prototype.__alloPatched) return;
      const originalShow = QuickBuy.prototype.show;
      QuickBuy.prototype.show = function (...args) {
        if (this.__alloShowing) return this.__alloShowing;
        let result;
        const done = () => { this.__alloShowing = null; };
        this.__alloShowing = new Promise((resolve) => {
          setTimeout(() => {
            Promise.resolve(result).then(resolve, resolve);
          }, 0);
        });
        this.__alloShowing.then(done, done);
        setTimeout(done, 6000);
        result = originalShow.apply(this, args);
        return result;
      };
      QuickBuy.prototype.__alloPatched = true;
    });
  }

  /* ------------------------------------------------------------------
     8b. Fenêtre d'achat rapide : points de la galerie photo
     ------------------------------------------------------------------ */
  const syncQbxDots = (slides) => {
    const dots = slides.parentElement && slides.parentElement.querySelectorAll('[data-allo-qbx-dot]');
    if (!dots || !dots.length) return;
    const index = Math.round(slides.scrollLeft / Math.max(1, slides.clientWidth));
    dots.forEach((dot, i) => dot.classList.toggle('is-on', i === index));
  };
  document.addEventListener('scroll', (e) => {
    const el = e.target;
    if (el && el.matches && el.matches('[data-allo-qbx-slides]')) {
      cancelAnimationFrame(el._alloRaf);
      el._alloRaf = requestAnimationFrame(() => syncQbxDots(el));
    }
  }, true);
  document.addEventListener('click', (e) => {
    const dot = e.target.closest && e.target.closest('[data-allo-qbx-dot]');
    if (!dot) return;
    const slides = dot.closest('.allo-qbx__media').querySelector('[data-allo-qbx-slides]');
    if (slides) slides.scrollTo({ left: slides.clientWidth * parseInt(dot.dataset.alloQbxDot, 10), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  });

  /* ------------------------------------------------------------------
     9. Initialisation + éditeur de thème + contenus rechargés
     ------------------------------------------------------------------ */
  const scanAll = (scope = document) => {
    scanReveal(scope);
    scanEdges(scope);
    scanMarquees(scope);
  };
  const init = () => scanAll(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  const rescan = debounce(() => scanAll(document), 120);
  ['shopify:section:load', 'cart-drawer:refreshed', 'cart:change', 'dialog:after-show'].forEach((evt) => {
    document.addEventListener(evt, () => setTimeout(rescan, 30));
  });
  if ('MutationObserver' in window) {
    new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.addedNodes.length) { rescan(); break; }
      }
    }).observe(document.body || root, { childList: true, subtree: true });
  }
})();
